<script setup lang="ts">
import { type ComponentPublicInstance, computed, ref } from 'vue';
import {
  arrow,
  autoUpdate,
  flip,
  limitShift,
  type Middleware,
  offset as floatingOffset,
  shift,
  size,
  useFloating,
} from '@floating-ui/vue';
import TooltipArrowIcon from '@/assets/icons/ui/tooltip-arrow.svg';
import type { Placement } from '@/modules/common/types/placement.ts';
import { HINT_TOOLTIP_PADDING_X, HINT_TOOLTIP_PADDING_Y } from '@/modules/common/consts/ui.ts';
import { injectInModal } from '@/providers/in-modal.ts';

const {
  anchor,
  show,
  placement,
  maxWidth,
  altBoundary = false,
  variant = 'default',
  overlay,
  overlayAlign = 'start',
  offset = 10,
  interactive = false,
} = defineProps<{
  anchor: HTMLElement | ComponentPublicInstance | undefined;
  show: boolean;
  placement: Placement;
  maxWidth: number;
  altBoundary?: boolean;
  variant?: 'default' | 'hint';
  overlay?: boolean;
  overlayAlign?: 'start' | 'end';
  offset?: number;
  interactive?: boolean;
}>();

const emit = defineEmits<{
  afterLeave: [];
  contentEnter: [];
  contentLeave: [];
}>();

function onContentPointerEnter() {
  if (!interactive) {
    return;
  }

  emit('contentEnter');
}

function onContentPointerLeave() {
  if (!interactive) {
    return;
  }

  emit('contentLeave');
}

const anchorRef = computed(() => anchor);
const dropdownRef = ref<HTMLElement>();
const arrowRef = ref<SVGElement>();

const inModal = injectInModal();

const hintPadding = `${HINT_TOOLTIP_PADDING_Y}px ${HINT_TOOLTIP_PADDING_X}px`;

const effectivePlacement = computed<Placement>(() => (overlay ? `bottom-${overlayAlign}` : placement));

const overlayMiddleware = computed((): Middleware[] => [
  floatingOffset(({ rects }) => ({
    mainAxis: -(rects.reference.height + rects.floating.height) / 2,
    crossAxis: overlayAlign === 'end' ? HINT_TOOLTIP_PADDING_X : -HINT_TOOLTIP_PADDING_X,
  })),
]);

const middleware = computed((): Middleware[] => {
  if (overlay) {
    return overlayMiddleware.value;
  }

  const flipMiddleware = flip({
    padding: 8,
    fallbackAxisSideDirection: 'end',
    altBoundary,
  });
  const shiftMiddleware = shift({
    padding: 8,
    limiter: limitShift({ offset: 36 }),
    altBoundary,
  });
  const preferFlip = altBoundary || placement.includes('-');

  return [
    floatingOffset(offset),
    preferFlip ? flipMiddleware : shiftMiddleware,
    preferFlip ? shiftMiddleware : flipMiddleware,
    arrow({ element: arrowRef, padding: 12 }),
    size({
      altBoundary,
      apply({ availableWidth, elements }) {
        const availableTooltipWidth = availableWidth - 16;
        const tooltipWidth = maxWidth ? Math.min(availableTooltipWidth, maxWidth) : availableTooltipWidth;

        Object.assign(elements.floating.style, {
          maxWidth: `${tooltipWidth}px`,
        });
      },
    }),
  ];
});

const {
  floatingStyles,
  middlewareData,
  placement: finalPlacement,
} = useFloating(anchorRef, dropdownRef, {
  placement: effectivePlacement,
  whileElementsMounted: autoUpdate,
  middleware,
});

const dropdownStyle = computed(() => ({
  ...floatingStyles.value,
  ...(variant === 'hint' ? { padding: hintPadding } : {}),
}));

const arrowStyles = computed(() => {
  const arrow = middlewareData.value.arrow;

  const dynamicSide = finalPlacement.value.split('-')[0];
  const isVertical = dynamicSide === 'top' || dynamicSide === 'bottom';

  const staticSide =
    {
      bottom: 'top',
      left: 'right',
      top: 'bottom',
      right: 'left',
    }[dynamicSide] || 'bottom';

  const staticSideOffset = isVertical ? 9 : 15;

  const rotation =
    {
      bottom: 0,
      left: 90,
      top: 180,
      right: 270,
    }[dynamicSide] || 0;

  return {
    left: arrow?.x != null ? `${arrow.x}px` : '',
    top: arrow?.y != null ? `${arrow.y}px` : '',
    [staticSide]: `-${staticSideOffset}px`,
    transform: `rotate(${rotation}deg)`,
  };
});
</script>

<template>
  <teleport to="body">
    <Transition
      appear
      name="opacity-fast"
      @after-leave="emit('afterLeave')"
    >
      <div
        v-if="show"
        ref="dropdownRef"
        class="dropdown"
        :class="{ '_in-modal': inModal, '_hint': variant === 'hint', '_interactive': interactive }"
        :data-ui-tooltip-content="interactive ? '' : undefined"
        :style="dropdownStyle"
        @pointerenter="onContentPointerEnter"
        @pointerleave="onContentPointerLeave"
      >
        <slot />

        <TooltipArrowIcon
          v-if="variant !== 'hint'"
          ref="arrowRef"
          class="arrow"
          :style="arrowStyles"
        />
      </div>
    </Transition>
  </teleport>
</template>

<style scoped lang="scss">
.dropdown,
.arrow {
  position: absolute;
  z-index: var(--z-index-tooltip);
  pointer-events: none;
}

.dropdown {
  width: max-content;
  padding: 12px;
  border: var(--border-yellow-500);
  border-radius: 8px;
  background-color: var(--color-yellow-100);
  overflow-wrap: break-word;

  &._interactive {
    pointer-events: auto;

    .arrow {
      pointer-events: auto;
    }
  }

  &._in-modal {
    &,
    .arrow {
      z-index: var(--z-index-modal-tooltip);
    }
  }

  &._hint {
    border: none;
    border-radius: 4px;
    background-color: var(--color-white);
    box-shadow: 0 0 8px 0 var(--color-gray-300);

    :slotted(.text) {
      color: var(--color-gray-1000);
      line-height: var(--line-height-l);
    }

    :slotted(.overlay-label) {
      color: var(--color-gray-500);
    }
  }
}
</style>
